import { useCallback, useEffect, useState } from "react";

/** The two themes this application defines tokens for (`tokens.css`). */
export type Theme = "light" | "dark";

const storageKey = "rws-ai-insights.theme";

/**
 * Reads a previously chosen theme back from local storage. Wrapped in a `try`/`catch` because
 * storage access can throw in a locked-down browsing context (e.g. a private window with storage
 * disabled) — that must not crash the application, it should just fall back to the OS preference.
 *
 * @returns The stored theme, or `undefined` if none was stored or storage is unavailable.
 */
function readStoredTheme(): Theme | undefined {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === "light" || stored === "dark" ? stored : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Determines the theme to render initially: an explicit choice the user made previously, or the
 * operating system's preference when they have not chosen one yet.
 */
function readInitialTheme(): Theme {
  return readStoredTheme() ?? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
}

/**
 * Manages the application's light/dark theme, reflected onto `<html data-theme>` so `tokens.css`
 * can select it, and persisted across visits. A manual choice always wins over the OS preference,
 * because that is what lets `agent-browser` (and a person) exercise both themes deterministically
 * regardless of what the host OS reports.
 *
 * @returns The current theme and a function to toggle it.
 */
export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      window.localStorage.setItem(storageKey, theme);
    } catch {
      // Storage being unavailable does not stop the theme from applying for this page view.
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggleTheme };
}
